import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  Users,
  Shield,
  Building,
  X,
  Languages,
  Activity,
  CloudUpload,
  Image,
  Crop,
  RotateCcw,
  Sun,
  Moon,
  Clock,
  ListOrdered,
  Mic,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  History,
  Eye,
  Upload,
  Trash2,
  UserPlus,
  ChevronRight,
  Briefcase,
  BarChart2,
  Settings
} from 'lucide-react';
import AptiviewLogo from '../../../../assets/AptiviewLogo.png';
import api from '../../../../api/client';
import { setDocumentDirection } from '../../../../utils/rtlUtils';
import LanguageSelectionModal from '../../../Sidebar/components/modals/LanguageSelectionModal';
import CompanyUsersCards from './CompanyUsersCards';
import CompanyUsersTable from './CompanyUsersTable';
import CompanyStatsCards from './CompanyStatsCards';
import AptiviewAppStatus from './AptiviewAppStatus';
import InviteAdminModal from './InviteAdminModal';
import UserLoginActivityModal from './UserLoginActivityModal';
import CreditsTab from './CreditsTab';
import NotificationPopup from '../../component/NotificationPopup';
import ConfirmationDialog from '../../../common/ConfirmationDialog';
import Timeline from '../../component/Timeline';

const DEFAULT_LANGUAGES = [
  { code: 'en', name: 'English', native_name: 'English' },
  { code: 'hi', name: 'Hindi', native_name: 'Hindi' },
  { code: 'bn', name: 'Bengali', native_name: 'বাংলা' },
  { code: 'mr', name: 'Marathi', native_name: 'मराठी' },
  { code: 'ar', name: 'Arabic', native_name: 'العربية' },
  { code: 'es', name: 'Spanish', native_name: 'Español' },
  { code: 'fr', name: 'French', native_name: 'Français' },
  { code: 'pt', name: 'Portuguese', native_name: 'Português' },
  { code: 'zh', name: 'Chinese', native_name: '中文' },
  { code: 'ja', name: 'Japanese', native_name: '日本語' },
  { code: 'ko', name: 'Korean', native_name: '한국어' }
];

const MIN_MAX_NO_OF_QUESTIONS = 5;
const INTERVIEW_DURATION_DEFAULT = 30;
const INTERVIEW_DURATION_MIN = 15;
const INTERVIEW_DURATION_MAX = 60;

const parseRequireAudioVerification = (value, defaultValue = true) => {
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  if (value === true || value === 'true' || value === 1 || value === '1') return true;
  return defaultValue;
};

const VALID_COMPANY_ADMIN_TABS = ['users', 'credits', 'languages', 'appStatus', 'interviewConfiguration', 'companyLogo'];

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

const CompanyAdmins = ({ adminToken, onLogout }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const isRTL = document.documentElement.dir === 'rtl' || i18n.language === 'ar';
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (() => {
    const tab = searchParams.get('tab') || 'users';
    return VALID_COMPANY_ADMIN_TABS.includes(tab) ? tab : 'users';
  })();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('admin');
  const [userDetails, setUserDetails] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [languages, setLanguages] = useState([]);
  const [languagesLoading, setLanguagesLoading] = useState(false);
  const [languagesError, setLanguagesError] = useState(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [langModalOpen, setLangModalOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, userId: null, userName: '' });
  const [loginActivityModal, setLoginActivityModal] = useState({
    open: false,
    userId: null,
    userName: '',
    companyId: null
  });

  // Timeline state
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timelineUserId, setTimelineUserId] = useState(null);
  const [timelineUserName, setTimelineUserName] = useState('');

  // Notification
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Logo Tab States
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [logoFileInfo, setLogoFileInfo] = useState(null);
  const [logoValidationError, setLogoValidationError] = useState(null);
  const [logoMode, setLogoMode] = useState('light'); // 'light' | 'dark'
  const [showNavbarPreview, setShowNavbarPreview] = useState(false);
  const [timePerQuestionInput, setTimePerQuestionInput] = useState('');
  const [interviewDurationInput, setInterviewDurationInput] = useState(String(INTERVIEW_DURATION_DEFAULT));
  const [requireAudioVerification, setRequireAudioVerification] = useState(true);
  const [interviewConfigErrors, setInterviewConfigErrors] = useState({});
  const [isInterviewConfigSaving, setIsInterviewConfigSaving] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserDetails(user);

    // Check access permissions
    const isSuperAdmin = user?.is_superuser === true;
    const isCompanyAdmin = Array.isArray(user?.admin_companies) && user.admin_companies.length > 0;

    if (!isSuperAdmin && !isCompanyAdmin) {
      setError(t('companyAdmins.accessDeniedMessage'));
      setLoading(false);
      return;
    }

    fetchCompanies();
    fetchLanguages();
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    []);

  useEffect(() => {
    if (selectedCompany) {
      fetchUsers(selectedCompany);
    }
  },// eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompany]);


  // Back-compat: redirect location.state.activeTab to query URL
  useEffect(() => {
    const requestedTab = location?.state?.activeTab;
    if (requestedTab && VALID_COMPANY_ADMIN_TABS.includes(requestedTab)) {
      navigate(`/companyadmins?tab=${requestedTab}`, { replace: true, state: {} });
    }
  }, [location?.state, navigate]);

  useEffect(() => {
    if (!userDetails) {
      return;
    }
    const invite = searchParams.get('invite');
    if (invite !== '1') {
      return;
    }
    if (userDetails.is_usermanager) {
      setIsInviteModalOpen(true);
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('invite');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, userDetails]);

  useEffect(() => {
    const activeCompany = companies.find(company => company.id === selectedCompany);
    const interviewSettings = activeCompany?.settings?.interview_settings || {};
    const maxNoOfQuestions = interviewSettings.max_no_of_questions;
    const interviewDurationMinutes = interviewSettings.interview_duration_minutes;

    setTimePerQuestionInput(
      maxNoOfQuestions == null || maxNoOfQuestions === ''
        ? ''
        : String(maxNoOfQuestions)
    );
    setInterviewDurationInput(
      interviewDurationMinutes == null || interviewDurationMinutes === ''
        ? String(INTERVIEW_DURATION_DEFAULT)
        : String(interviewDurationMinutes)
    );
    setRequireAudioVerification(parseRequireAudioVerification(interviewSettings.require_audio_verification, true));
    setInterviewConfigErrors({});
  }, [companies, selectedCompany]);

  const fetchLanguages = async () => {
    try {
      setLanguagesLoading(true);
      setLanguagesError(null);
      const response = await api.get('/languages/');
      const languagesData = response.data.results || response.data || [];
      setLanguages(languagesData);
    } catch (err) {
      console.error('Error fetching languages:', err);
      setLanguages(DEFAULT_LANGUAGES);
      setLanguagesError(t('companyAdmins.failedToLoadLanguages'));
    } finally {
      setLanguagesLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies/');
      const companiesData = response.data.results || response.data;
      setCompanies(companiesData);

      // Auto-select company if only one company is available
      if (companiesData.length === 1) {
        setSelectedCompany(companiesData[0].id);
      } else if (userDetails && !userDetails.is_superuser && userDetails.admin_companies?.length === 1) {
        // For company admins with exactly one admin company, find and select it
        const userCompany = companiesData.find(company =>
          userDetails.admin_companies.includes(company.name)
        );
        if (userCompany) {
          setSelectedCompany(userCompany.id);
        }
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError(t('companyAdmins.failedToLoadCompanies'));
      setSnackbar({
        open: true,
        message: t('companyAdmins.failedToLoadCompanies'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = useCallback(async (companyId) => {
    try {
      setLoading(true);
      const response = await api.get(`/companies/${companyId}/users?role=admin`);
      setUsers(response.data.results || response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(t('companyAdmins.failedToLoadUsers'));
      setUsers([]);
      setSnackbar({
        open: true,
        message: t('companyAdmins.failedToLoadUsers'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleCompanyChange = (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId ? Number(companyId) : null);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const handleRoleFilter = (value) => {
    setRoleFilter(value);
  };

  const handleSendInvite = (email, successMessage) => {
    setSnackbar({
      open: true,
      message: successMessage || t('companyAdmins.successInviteSent', 'Invitation sent to {{email}} successfully.', { email }),
      severity: 'success'
    });
  };

  const openDeleteDialog = (userId, userName) => {
    setDeleteDialog({ open: true, userId, userName });
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await api.delete(`/v1/admin/remove-admin/${deleteDialog.userId}/`);
      setSnackbar({
        open: true,
        message: t('companyAdmins.adminRemovedSuccess', 'Admin {{name}} removed successfully.', { name: deleteDialog.userName }),
        severity: 'success'
      });
      // Refresh user list
      if (selectedCompany) {
        fetchUsers(selectedCompany);
      }
    } catch (err) {
      console.error('Error removing admin:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.failedToRemoveAdmin', 'Failed to remove admin.'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, userId: null, userName: '' });
    }
  };

  const handleToggleUserManager = async (userId, newValue) => {
    try {
      const response = await api.patch(`/v1/admin/toggle-user-manager/${userId}/`, {
        usermanager: newValue
      });

      setSnackbar({
        open: true,
        message: response.data?.message || t('companyAdmins.userManagerUpdated', 'User Manager status updated successfully.'),
        severity: 'success'
      });

      // Refresh user list
      if (selectedCompany) {
        fetchUsers(selectedCompany);
      }
    } catch (err) {
      console.error('Error toggling user manager status:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.failedToUpdateUserManager', 'Failed to update User Manager status.'),
        severity: 'error'
      });
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
  };

  // Client-side filtering
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      user.user.firstName?.toLowerCase().includes(searchLower) ||
      user.user.lastName?.toLowerCase().includes(searchLower) ||
      user.user.email?.toLowerCase().includes(searchLower) ||
      user.user.username?.toLowerCase().includes(searchLower) ||
      (user.user.profile?.mobile && user.user.profile.mobile.includes(searchTerm)) ||
      (user.user.profile?.aadhaar_number && user.user.profile.aadhaar_number.includes(searchTerm));

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Sort users based on sortConfig
  const sortedUsers = React.useMemo(() => {
    if (!sortConfig.key) return filteredUsers;

    return [...filteredUsers].sort((a, b) => {
      let aValue, bValue;

      // Handle nested properties
      if (sortConfig.key.includes('.')) {
        const keys = sortConfig.key.split('.');
        aValue = keys.reduce((obj, key) => obj?.[key], a);
        bValue = keys.reduce((obj, key) => obj?.[key], b);
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }

      // Handle string comparison
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortConfig]);

  const availableUiLanguages = useMemo(() => {
    const fromUser = userDetails?.available_languages;
    if (Array.isArray(fromUser) && fromUser.length > 1) {
      return fromUser;
    }
    return ['en', 'hi', 'ar', 'fr', 'es', 'zh', 'ja', 'ko', 'bn', 'pt', 'mr'];
  }, [userDetails?.available_languages]);

  const uiLanguageOptions = useMemo(
    () => [
      { code: 'en', name: 'English', flag: 'https://flagcdn.com/w40/gb.png' },
      { code: 'hi', name: 'हिंदी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'bn', name: 'বাংলা', flag: 'https://flagcdn.com/w40/bd.png' },
      { code: 'mr', name: 'मराठी', flag: 'https://flagcdn.com/w40/in.png' },
      { code: 'ar', name: 'العربية', flag: 'https://flagcdn.com/w40/sa.png' },
      { code: 'es', name: 'Español', flag: 'https://flagcdn.com/w40/es.png' },
      { code: 'fr', name: 'Français', flag: 'https://flagcdn.com/w40/fr.png' },
      { code: 'pt', name: 'Português', flag: 'https://flagcdn.com/w40/br.png' },
      { code: 'zh', name: '中文', flag: 'https://flagcdn.com/w40/cn.png' },
      { code: 'ja', name: '日本語', flag: 'https://flagcdn.com/w40/jp.png' },
      { code: 'ko', name: '한국어', flag: 'https://flagcdn.com/w40/kr.png' },
    ].filter((lang) => availableUiLanguages.includes(lang.code)),
    [availableUiLanguages]
  );

  const userLanguagesForModal = useMemo(
    () =>
      uiLanguageOptions.map((lang) => ({
        code: lang.code,
        name: lang.name,
        flag: (
          <img
            src={lang.flag}
            width="24"
            height="24"
            style={{ borderRadius: '50%', objectFit: 'cover', border: '1px solid #e2e8f0' }}
            alt={lang.name}
          />
        ),
      })),
    [uiLanguageOptions]
  );

  const currentUiLanguage =
    uiLanguageOptions.find((lang) => lang.code === i18n.language) || uiLanguageOptions[0];

  const getFilteredCompanies = () => {
    if (!userDetails) return companies.filter(company => company.name !== 'Practice Company');

    // Super admins can see all companies (including Practice Company)
    if (userDetails.is_superuser) return companies;

    // Company admins can only see their admin companies, and should never see Practice Company
    if (userDetails.admin_companies && userDetails.admin_companies.length > 0) {
      return companies.filter(company =>
        company.name !== 'Practice Company' && userDetails.admin_companies.includes(company.name)
      );
    }

    return [];
  };

  const getUserStats = () => {
    const totalCount = filteredUsers.length;
    const adminCount = filteredUsers.filter(u => u.role === 'admin').length;
    const candidateCount = filteredUsers.filter(u => u.role === 'candidate').length;
    const employeeCount = filteredUsers.filter(u => u.role === 'employee').length;

    return { totalCount, adminCount, candidateCount, employeeCount };
  };

  const enabledLanguageCodes = userDetails?.available_languages || [];
  const filteredCompanies = getFilteredCompanies();
  const stats = getUserStats();

  if (error && !loading) {
    return (
      <div className="min-h-screen" style={{ paddingTop: '10px' }}>
        <div className="p-6">
          <div className="max-w-2xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="h-6 w-6 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-red-800">{t('companyAdmins.accessDenied')}</h3>
                  <p className="text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getActiveSectionTitle = () => {
    switch (activeTab) {
      case 'users':
        return t('companyAdmins.tabUserManagement');
      case 'credits':
        return t('companyAdmins.creditsTab.tabTitle', 'Credits');
      case 'languages':
        return t('companyAdmins.tabLanguages');
      case 'appStatus':
        return t('companyAdmins.tabAppStatus');
      case 'interviewConfiguration':
        return t('companyAdmins.interviewConfig.title', 'Interview Configuration');
      case 'companyLogo':
        return t('companyAdmins.tabCompanyLogo');
      default:
        return t('companyAdmins.tabUserManagement');
    }
  };

  const getActiveSectionDescription = () => {
    switch (activeTab) {
      case 'users':
        return t('companyAdmins.pageSubtitle');
      case 'credits':
        return t(
          'companyAdmins.creditsTab.headerDescription',
          'View available credits, track usage and expirations, and review detailed credit activity for your account.'
        );
      case 'languages':
        return t('companyAdmins.languagesSectionDescription');
      case 'appStatus':
        return t('companyAdmins.appStatusDescription');
      case 'interviewConfiguration':
        return t(
          'companyAdmins.interviewConfig.description',
          'Set default question limits, interview length, and whether spoken microphone verification is required. New invites use these values unless you override them.'
        );
      case 'companyLogo':
        return t('companyAdmins.companyLogoDescription');
      default:
        return t('companyAdmins.pageSubtitle');
    }
  };

  const renderSectionHeader = () => (
    <div className="px-6 pt-5 pb-4 border-b border-gray-200 bg-gray-50">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <h2
            className="text-base font-semibold text-gray-900"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {getActiveSectionTitle()}
          </h2>
          <p
            className="text-sm text-gray-600 mt-1"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {getActiveSectionDescription()}
          </p>
        </div>
        {activeTab === 'users' && userDetails?.is_usermanager && (
          <button
            type="button"
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white shadow-md transition-all duration-200 hover:opacity-90 hover:scale-[1.02] flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)',
              fontFamily: "'Poppins', sans-serif",
            }}
          >
            <UserPlus size={18} />
            {t('companyAdmins.inviteAdmin', 'Invite Admin')}
          </button>
        )}
        {activeTab === 'languages' && currentUiLanguage && (
          <button
            type="button"
            onClick={() => setLangModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border border-gray-200 bg-white text-gray-700 shadow-sm transition-all duration-200 hover:border-sky-300 hover:text-sky-700 flex-shrink-0"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {currentUiLanguage.flag && (
              <img
                src={currentUiLanguage.flag}
                width="22"
                height="22"
                alt=""
                className="rounded-full object-cover border border-gray-200"
              />
            )}
            <span>
              {currentUiLanguage.name} ({currentUiLanguage.code.toUpperCase()})
            </span>
            <ChevronRight size={18} className="text-gray-400" />
          </button>
        )}
      </div>
    </div>
  );

  const renderLanguagesTab = () => {
    return (
      <div className="p-6">
        <div className="mb-6">
          {enabledLanguageCodes.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-2 text-sm text-blue-800">
              {t('companyAdmins.languagesNote')}
            </div>
          )}
        </div>

        {languagesError && (
          <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            {languagesError}
          </div>
        )}

        {languagesLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div
                key={index}
                className="h-24 animate-pulse rounded-xl border border-gray-200 bg-gray-100"
              />
            ))}
          </div>
        ) : languages.length === 0 ? (
          <div className="text-center py-12">
            <Languages size={48} className="mx-auto mb-4 opacity-50 text-gray-400" />
            <p className="text-lg text-gray-600">{t('companyAdmins.noLanguagesFound')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {languages.map((language) => {
              const isEnabled = enabledLanguageCodes.includes(language.code);
              return (
                <div
                  key={language.code}
                  className={`rounded-xl border p-4 shadow-sm transition-all hover:shadow-md ${isEnabled
                    ? 'border-green-200 bg-gradient-to-br from-green-50 to-white'
                    : 'border-gray-200 bg-white'
                    }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900 mb-1">{language.name}</p>
                      {language.native_name && (
                        <p className="text-sm text-gray-600 mb-1">{language.native_name}</p>
                      )}
                      <p className="text-xs uppercase tracking-wide text-gray-500">
                        {language.code}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${isEnabled
                        ? 'bg-green-100 text-green-700 border border-green-200'
                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }`}
                    >
                      {isEnabled
                        ? t('companyAdmins.enabledBySuperAdmin')
                        : t('companyAdmins.notEnabledBySuperAdmin')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const handleVerificationRequiredChange = async (companyId, value) => {
    try {
      const { data } = await api.patch(`/companies/${companyId}/`, { verification_required: value });
      setCompanies(prev =>
        prev.map(c => {
          if (c.id !== Number(companyId)) return c;
          const updated = { ...c, verification_required: data.verification_required, showing_identity: data.showing_identity };
          if (updated.verification_required === false) updated.showing_identity = false;
          return updated;
        })
      );
      setSnackbar({
        open: true,
        message: t('companyAdmins.verificationRequiredUpdated'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating verification_required:', err);
      const message =
        err.response?.status === 403
          ? t('companyAdmins.verificationRequiredForbidden')
          : err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.verificationRequiredUpdateFailed');
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    }
  };

  const handleShowingIdentityChange = async (companyId, value) => {
    try {
      const { data } = await api.patch(`/companies/${companyId}/`, { showing_identity: value });
      setCompanies(prev =>
        prev.map(c => (c.id === Number(companyId) ? { ...c, showing_identity: data.showing_identity } : c))
      );
      setSnackbar({
        open: true,
        message: t('companyAdmins.showingIdentityUpdated'),
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating showing_identity:', err);
      const message =
        err.response?.data?.showing_identity?.[0] ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        t('companyAdmins.showingIdentityUpdateFailed');
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
    }
  };

  const renderAppStatusTab = () => {
    const isSA = userDetails?.is_superuser === true;
    return (
      <div className="p-6">
        {/* Company selection for App Status (verification setting is per company) */}
        <div className="mb-6">
          <label className="block text-gray-700 mb-2 text-sm font-medium">
            {isSA
              ? t('companyAdmins.selectConfigurationScope', 'Select Configuration Scope')
              : t('companyAdmins.selectCompany')}
          </label>
          <select
            value={selectedCompany || ''}
            onChange={handleCompanyChange}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-sm"
          >
            <option value="">
              {isSA
                ? t('companyAdmins.chooseScope', 'Choose a configuration scope')
                : t('companyAdmins.chooseCompany')}
            </option>
            {filteredCompanies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name === 'Practice Company'
                  ? t('companyAdmins.practiceInterviewsB2c', 'Practice Interviews (B2C)')
                  : company.name}
              </option>
            ))}
          </select>
        </div>
        <AptiviewAppStatus
          selectedCompany={selectedCompany}
          companies={companies}
          onVerificationRequiredChange={handleVerificationRequiredChange}
          onShowingIdentityChange={handleShowingIdentityChange}
          isSuperAdmin={isSA}
        />
      </div>
    );
  };

  const validateInterviewConfiguration = () => {
    const nextErrors = {};
    const trimmedTimePerQuestion = timePerQuestionInput.trim();
    const trimmedInterviewDuration = interviewDurationInput.trim();

    if (trimmedTimePerQuestion !== '') {
      const parsedTimePerQuestion = Number(trimmedTimePerQuestion);
      if (!Number.isFinite(parsedTimePerQuestion) || !Number.isInteger(parsedTimePerQuestion)) {
        nextErrors.timePerQuestion = t('companyAdmins.interviewConfig.timePerQuestionIntegerError', 'Please enter a whole number of questions.');
      } else if (parsedTimePerQuestion < MIN_MAX_NO_OF_QUESTIONS) {
        nextErrors.timePerQuestion = t(
          'companyAdmins.interviewConfig.timePerQuestionMinError',
          'Number of questions must be at least {{min}}.',
          { min: MIN_MAX_NO_OF_QUESTIONS }
        );
      }
    }

    const parsedInterviewDuration = Number(trimmedInterviewDuration);
    if (!Number.isFinite(parsedInterviewDuration) || !Number.isInteger(parsedInterviewDuration)) {
      nextErrors.interviewDuration = t('companyAdmins.interviewConfig.interviewDurationIntegerError', 'Please enter a whole number of minutes.');
    } else if (parsedInterviewDuration < INTERVIEW_DURATION_MIN || parsedInterviewDuration > INTERVIEW_DURATION_MAX) {
      nextErrors.interviewDuration = t(
        'companyAdmins.interviewConfig.interviewDurationRangeError',
        'Interview duration must be between {{min}} and {{max}} minutes.',
        { min: INTERVIEW_DURATION_MIN, max: INTERVIEW_DURATION_MAX }
      );
    }

    return nextErrors;
  };

  const handleSaveInterviewConfiguration = async () => {
    if (!selectedCompany) return;

    const nextErrors = validateInterviewConfiguration();
    setInterviewConfigErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) return;

    const activeCompany = companies.find(company => company.id === selectedCompany);
    const existingSettings = activeCompany?.settings || {};
    const existingInterviewSettings = existingSettings.interview_settings || {};
    const trimmedTimePerQuestion = timePerQuestionInput.trim();
    const trimmedInterviewDuration = interviewDurationInput.trim();

    const payload = {
      settings: {
        ...existingSettings,
        interview_settings: {
          ...existingInterviewSettings,
          max_no_of_questions: trimmedTimePerQuestion === '' ? null : Number(trimmedTimePerQuestion),
          interview_duration_minutes: Number(trimmedInterviewDuration),
          require_audio_verification: requireAudioVerification
        }
      }
    };

    try {
      setIsInterviewConfigSaving(true);
      const { data } = await api.patch(`/companies/${selectedCompany}/`, payload);
      setCompanies(prevCompanies =>
        prevCompanies.map(company => (
          company.id === selectedCompany
            ? { ...company, settings: data.settings || payload.settings }
            : company
        ))
      );
      setSnackbar({
        open: true,
        message: t('companyAdmins.interviewConfig.saveSuccess', 'Interview configuration updated successfully.'),
        severity: 'success'
      });
      window.dispatchEvent(new CustomEvent('company-data-updated'));
    } catch (err) {
      console.error('Error updating interview configuration:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t('companyAdmins.interviewConfig.saveError', 'Failed to update interview configuration.'),
        severity: 'error'
      });
    } finally {
      setIsInterviewConfigSaving(false);
    }
  };

  const renderInterviewConfigurationTab = () => {
    const isUnlimited = timePerQuestionInput.trim() === '';
    const durationNum = Number(interviewDurationInput);
    const durationDisplay = Number.isFinite(durationNum) ? durationNum : INTERVIEW_DURATION_DEFAULT;

    const questionsSummary = isUnlimited
      ? t('companyAdmins.interviewConfig.summaryUnlimitedQuestions', 'Unlimited questions')
      : t('companyAdmins.interviewConfig.summaryQuestions', '{{count}} questions', { count: Number(timePerQuestionInput) });
    const durationSummary = t('companyAdmins.interviewConfig.summaryMinutes', '{{count}} min', { count: durationDisplay });
    const audioSummary = requireAudioVerification
      ? t('companyAdmins.interviewConfig.summaryAudioOn', 'Audio verification on')
      : t('companyAdmins.interviewConfig.summaryAudioOff', 'Audio verification off');

    const settingCardClass =
      'relative overflow-hidden rounded-2xl border border-gray-200/90 bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md';

    return (
      <div className="p-6 sm:p-8">
        <div className="mb-8 rounded-2xl border border-gray-200/80 bg-gradient-to-br from-slate-50/90 to-white p-5 shadow-sm sm:p-6">
          <label htmlFor="interview-config-company" className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Building size={18} className="text-gray-500" aria-hidden />
            {t('companyAdmins.selectCompany')}
          </label>
          <select
            id="interview-config-company"
            value={selectedCompany || ''}
            onChange={handleCompanyChange}
            className="w-full max-w-lg rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm transition-all focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/25"
          >
            <option value="">{t('companyAdmins.chooseCompany')}</option>
            {filteredCompanies.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </div>

        {!selectedCompany ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 px-6 py-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white shadow-sm">
              <Building size={32} className="text-gray-300" aria-hidden />
            </div>
            <p className="font-medium text-gray-600">{t('companyAdmins.selectCompanyMessage')}</p>
            <p className="mx-auto mt-2 max-w-sm text-sm text-gray-500">
              {t(
                'companyAdmins.interviewConfig.selectCompanyHint',
                'Choose a company above to edit its interview defaults.'
              )}
            </p>
          </div>
        ) : (
          <div className="max-w-5xl space-y-6">
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-sky-100 bg-gradient-to-r from-sky-50/90 to-fuchsia-50/50 px-4 py-3.5 text-sm"
              role="status"
            >
              <span className="font-semibold text-sky-900">
                {t('companyAdmins.interviewConfig.previewLabel', 'Default preview')}
              </span>
              <span className="hidden sm:inline text-sky-300" aria-hidden>
                ·
              </span>
              <span className="text-sky-800">{questionsSummary}</span>
              <span className="text-sky-300" aria-hidden>
                ·
              </span>
              <span className="text-sky-800">{durationSummary}</span>
              <span className="text-sky-300" aria-hidden>
                ·
              </span>
              <span className="text-sky-800">{audioSummary}</span>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className={settingCardClass}>
                <div
                  className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-sky-500 to-fuchsia-500"
                  aria-hidden
                />
                <div className="flex gap-4 pl-2">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-sky-100 bg-sky-50">
                    <ListOrdered size={22} className="text-sky-600" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base font-semibold text-gray-900">
                      {t('companyAdmins.interviewConfig.numberOfQuestions', 'Number of questions')}
                    </h4>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">
                      {t(
                        'companyAdmins.interviewConfig.numberOfQuestionsHelp',
                        'Cap how many questions are asked. Leave empty for unlimited. When set, use at least {{min}}.',
                        { min: MIN_MAX_NO_OF_QUESTIONS }
                      )}
                    </p>
                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:flex-wrap">
                      <div className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-[220px]">
                        <label htmlFor="interview-max-questions" className="sr-only">
                          {t('companyAdmins.interviewConfig.numberOfQuestions', 'Number of questions')}
                        </label>
                        <div className="flex rounded-xl border border-gray-200 bg-gray-50/50 shadow-inner focus-within:border-sky-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-sky-500/20">
                          <input
                            id="interview-max-questions"
                            type="number"
                            min={MIN_MAX_NO_OF_QUESTIONS}
                            step={1}
                            value={timePerQuestionInput}
                            onChange={(e) => {
                              setTimePerQuestionInput(e.target.value);
                              if (interviewConfigErrors.timePerQuestion) {
                                setInterviewConfigErrors(prev => ({ ...prev, timePerQuestion: null }));
                              }
                            }}
                            placeholder={t('companyAdmins.interviewConfig.unlimitedPlaceholder', 'Unlimited')}
                            className="min-w-0 flex-1 rounded-xl border-0 bg-transparent px-4 py-2.5 text-sm font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
                          />
                          <span className="flex shrink-0 items-center pr-3 text-xs font-medium text-gray-400">
                            {t('companyAdmins.interviewConfig.questionsSuffix', 'questions')}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setTimePerQuestionInput('');
                          if (interviewConfigErrors.timePerQuestion) {
                            setInterviewConfigErrors(prev => ({ ...prev, timePerQuestion: null }));
                          }
                        }}
                        className="self-start rounded-lg px-3 py-2 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-50"
                      >
                        {t('companyAdmins.interviewConfig.useUnlimited', 'Use unlimited')}
                      </button>
                    </div>
                    <div className="mt-3">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          isUnlimited ? 'bg-violet-100 text-violet-800' : 'bg-sky-100 text-sky-800'
                        }`}
                      >
                        {isUnlimited
                          ? t('companyAdmins.interviewConfig.badgeUnlimited', 'Unlimited')
                          : t('companyAdmins.interviewConfig.badgeQuestions', '{{count}} questions', {
                              count: Number(timePerQuestionInput)
                            })}
                      </span>
                    </div>
                    {interviewConfigErrors.timePerQuestion && (
                      <p className="mt-2 text-sm text-red-600">{interviewConfigErrors.timePerQuestion}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className={settingCardClass}>
                <div
                  className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-fuchsia-500 to-sky-500"
                  aria-hidden
                />
                <div className="flex gap-4 pl-2">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-fuchsia-100 bg-fuchsia-50">
                    <Clock size={22} className="text-fuchsia-600" aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="text-base font-semibold text-gray-900">
                      {t('companyAdmins.interviewConfig.interviewDuration', 'Interview duration')}
                    </h4>
                    <p className="mt-1 text-sm leading-relaxed text-gray-600">
                      {t(
                        'companyAdmins.interviewConfig.interviewDurationHelp',
                        'Total time allowed for the interview. Default {{defaultValue}} min; allowed {{min}}–{{max}} min.',
                        {
                          defaultValue: INTERVIEW_DURATION_DEFAULT,
                          min: INTERVIEW_DURATION_MIN,
                          max: INTERVIEW_DURATION_MAX
                        }
                      )}
                    </p>
                    <div className="mt-4 flex flex-col gap-1.5 sm:max-w-[220px]">
                      <label htmlFor="interview-duration-mins" className="sr-only">
                        {t('companyAdmins.interviewConfig.interviewDuration', 'Interview duration')}
                      </label>
                      <div className="flex rounded-xl border border-gray-200 bg-gray-50/50 shadow-inner focus-within:border-fuchsia-400 focus-within:bg-white focus-within:ring-2 focus-within:ring-fuchsia-500/20">
                        <input
                          id="interview-duration-mins"
                          type="number"
                          min={INTERVIEW_DURATION_MIN}
                          max={INTERVIEW_DURATION_MAX}
                          step={1}
                          value={interviewDurationInput}
                          onChange={(e) => {
                            setInterviewDurationInput(e.target.value);
                            if (interviewConfigErrors.interviewDuration) {
                              setInterviewConfigErrors(prev => ({ ...prev, interviewDuration: null }));
                            }
                          }}
                          className="min-w-0 flex-1 rounded-xl border-0 bg-transparent px-4 py-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-0"
                        />
                        <span className="flex items-center pr-4 text-xs font-medium text-gray-400">
                          {t('companyAdmins.interviewConfig.minutesSuffix', 'min')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        {t(
                          'companyAdmins.interviewConfig.durationEffectiveHint',
                          'Candidates see this as a {{count}}-minute interview window.',
                          { count: durationDisplay }
                        )}
                      </p>
                    </div>
                    {interviewConfigErrors.interviewDuration && (
                      <p className="mt-2 text-sm text-red-600">{interviewConfigErrors.interviewDuration}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={settingCardClass}>
              <div
                className="pointer-events-none absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-emerald-500 to-sky-500"
                aria-hidden
              />
              <div className="flex gap-4 pl-2">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-100 bg-emerald-50">
                  <Mic size={22} className="text-emerald-600" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <h4 className="text-base font-semibold text-gray-900">
                        {t('companyAdmins.interviewConfig.requireAudioVerification', 'Require audio verification')}
                      </h4>
                      <p className="mt-1 text-sm leading-relaxed text-gray-600">
                        {t(
                          'companyAdmins.interviewConfig.requireAudioVerificationHelp',
                          'Candidates must record a spoken sentence to confirm their microphone before the interview. Recruiters can override this when scheduling an invite.'
                        )}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="text-sm text-gray-600">
                        {requireAudioVerification
                          ? t('companyAdmins.interviewConfig.audioVerificationOn', 'Required')
                          : t('companyAdmins.interviewConfig.audioVerificationOff', 'Optional')}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={requireAudioVerification}
                        onClick={() => setRequireAudioVerification(prev => !prev)}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
                          requireAudioVerification ? 'bg-sky-600' : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            requireAudioVerification ? 'translate-x-5' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4 border-t border-gray-100 pt-8 sm:flex-row sm:items-center sm:justify-between">
              <p className="max-w-md text-xs leading-relaxed text-gray-500">
                {t(
                  'companyAdmins.interviewConfig.saveHint',
                  'These defaults are stored on the server and apply to this company going forward.'
                )}
              </p>
              <button
                type="button"
                onClick={handleSaveInterviewConfiguration}
                disabled={isInterviewConfigSaving}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-200 ${
                  isInterviewConfigSaving ? 'cursor-not-allowed bg-gray-400 shadow-none' : 'hover:opacity-95 hover:shadow-md active:scale-[0.99]'
                }`}
                style={{
                  background: isInterviewConfigSaving ? undefined : 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)'
                }}
              >
                {isInterviewConfigSaving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" aria-hidden />
                    {t('companyAdmins.interviewConfig.saving', 'Saving...')}
                  </>
                ) : (
                  <>
                    <Save size={18} aria-hidden />
                    {t('companyAdmins.interviewConfig.save', 'Save configuration')}
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCompanyLogoTab = () => {
    const activeCompany = companies.find(c => c.id === selectedCompany);

    const resolveMediaUrl = (url) => {
      if (!url) return null;
      if (/^https?:\/\//i.test(url)) return url;
      const apiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api/`;
      return apiUrl.replace(/\/api\/?$/, '') + '/' + url.replace(/^\//, '');
    };

    const getImageInfo = (file) => {
      return new Promise((resolve) => {
        const img = new window.Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => resolve({ width: 0, height: 0 });
        img.src = URL.createObjectURL(file);
      });
    };

    const formatBytes = (bytes) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    const getFileTypeLabel = (type) => {
      const map = { 'image/png': 'PNG', 'image/jpeg': 'JPG', 'image/svg+xml': 'SVG', 'image/webp': 'WEBP' };
      return map[type] || type.split('/')[1]?.toUpperCase() || 'IMG';
    };

    const processFile = async (file) => {
      setLogoValidationError(null);
      const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type)) {
        setLogoValidationError(t('companyAdmins.unsupportedFormat'));
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        setLogoValidationError(t('companyAdmins.fileTooLarge'));
        return;
      }
      const { width, height } = await getImageInfo(file);
      setLogoFileInfo({ name: file.name, size: formatBytes(file.size), type: getFileTypeLabel(file.type), width, height });
      setSelectedLogoFile(file);
      setLogoPreviewUrl(URL.createObjectURL(file));
    };

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) processFile(file);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    };

    const handleUpload = async () => {
      if (!selectedLogoFile || !selectedCompany) return;
      setIsLogoUploading(true);
      const formData = new FormData();
      // Rename file to {companyname}-logo.{ext}
      const companySlug = (activeCompany?.name || 'company')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      const ext = selectedLogoFile.name.split('.').pop() || 'png';
      const renamedFile = new File([selectedLogoFile], `${companySlug}-logo.${ext}`, { type: selectedLogoFile.type });
      formData.append('logo', renamedFile);
      try {
        const response = await api.patch(`/companies/${selectedCompany}/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        setCompanies(prev => prev.map(c => c.id === selectedCompany ? { ...c, logo: response.data.logo } : c));
        setSnackbar({ open: true, message: t('companyAdmins.logoUpdateSuccess'), severity: 'success' });
        setSelectedLogoFile(null);
        setLogoPreviewUrl(null);
        setLogoFileInfo(null);
        window.dispatchEvent(new CustomEvent('company-data-updated'));
      } catch (err) {
        console.error('Error uploading logo:', err);
        setSnackbar({ open: true, message: t('companyAdmins.logoUpdateFailed'), severity: 'error' });
      } finally {
        setIsLogoUploading(false);
      }
    };

    const handleResetLogo = async () => {
      if (!selectedCompany) return;
      if (!window.confirm(t('companyAdmins.logoResetConfirm'))) return;
      try {
        await api.patch(`/companies/${selectedCompany}/`, { logo: null });
        setCompanies(prev => prev.map(c => c.id === selectedCompany ? { ...c, logo: null } : c));
        setSnackbar({ open: true, message: t('companyAdmins.logoResetSuccess'), severity: 'success' });
        window.dispatchEvent(new CustomEvent('company-data-updated'));
      } catch (err) {
        setSnackbar({ open: true, message: t('companyAdmins.logoResetFailed'), severity: 'error' });
      }
    };


    const currentLogoUrl = logoPreviewUrl || (activeCompany?.logo ? resolveMediaUrl(activeCompany.logo) : null);
    return (
      <div className="p-6">
        {/* Company Selector */}
        <div className="mb-6">
          <label className="block text-gray-700 mb-2 text-sm font-medium">{t('companyAdmins.selectCompany')}</label>
          <select
            value={selectedCompany || ''}
            onChange={handleCompanyChange}
            className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-sm"
          >
            <option value="">{t('companyAdmins.chooseCompany')}</option>
            {filteredCompanies.map((company) => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>
        </div>

        {!selectedCompany ? (
          <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl">
            <Building size={48} className="mx-auto mb-4 opacity-30 text-gray-400" />
            <p className="text-gray-500 font-medium">{t('companyAdmins.selectCompanyMessage')}</p>
          </div>
        ) : (
          <>
            {/* ── UPLOAD SECTION ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Preview Panel */}
              <div className="space-y-4">
                {/* Mode Toggle */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-gray-600">{t('companyAdmins.previewMode')}</span>
                  <button
                    onClick={() => setLogoMode('light')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${logoMode === 'light' ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    <Sun size={13} /> {t('companyAdmins.light')}
                  </button>
                  <button
                    onClick={() => setLogoMode('dark')}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${logoMode === 'dark' ? 'bg-gray-800 text-white border border-gray-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  >
                    <Moon size={13} /> {t('companyAdmins.dark')}
                  </button>
                </div>

                {/* Logo Preview Box */}
                <div className={`rounded-2xl border-2 border-dashed p-8 min-h-[200px] flex items-center justify-center transition-colors ${logoMode === 'dark' ? 'bg-gray-900 border-gray-600' : 'bg-white border-gray-300'}`}>
                  {currentLogoUrl ? (
                    <img src={currentLogoUrl} alt="Logo Preview" className="max-h-40 max-w-full object-contain" />
                  ) : (
                    <div className="text-center">
                      <Image size={48} className={`mx-auto mb-2 ${logoMode === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                      <p className={`text-sm ${logoMode === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>{t('companyAdmins.noLogoUploaded')}</p>
                    </div>
                  )}
                </div>

                {/* Navbar Preview */}
                <div>
                  <button
                    onClick={() => setShowNavbarPreview(v => !v)}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                  >
                    <Eye size={15} /> {showNavbarPreview ? t('companyAdmins.hideNavbarPreview') : t('companyAdmins.showNavbarPreview')}
                  </button>
                  {showNavbarPreview && (
                    <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                      {/* Simulated Navbar */}
                      <div className="flex items-center gap-2 px-4 py-3 bg-white border-b border-gray-100">
                        {/* Aptiview Platform Logo */}
                        <img src={AptiviewLogo} alt="Aptiview Logo" className="h-6 object-contain" />
                        
                        {/* Separator and Custom Company Logo */}
                        {currentLogoUrl && (
                          <>
                            <div className="h-5 w-px bg-gray-300 mx-1" />
                            <img src={currentLogoUrl} alt="Company Logo" className="h-6 object-contain max-w-[120px]" />
                          </>
                        )}
                        <div className="ml-auto flex gap-3">
                          <div className="w-16 h-2 bg-gray-200 rounded-full" />
                          <div className="w-10 h-2 bg-gray-200 rounded-full" />
                          <div className="w-12 h-2 bg-gray-200 rounded-full" />
                        </div>
                      </div>
                      {/* Simulated Sidebar */}
                      <div className="flex">
                        <div className="w-14 bg-gray-50 border-r border-gray-100 flex flex-col items-center py-3 gap-3">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400">
                            <Briefcase size={16} className="opacity-70" />
                          </div>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100 text-gray-400">
                            <BarChart2 size={16} className="opacity-70" />
                          </div>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-blue-50 text-blue-600 border border-blue-100">
                            <Settings size={16} />
                          </div>
                          <div className="w-6 h-1 bg-gray-200 rounded-full" />
                          <div className="w-6 h-1 bg-gray-200 rounded-full" />
                        </div>
                        <div className="flex-1 bg-gray-50 p-3 min-h-[60px]">
                          <div className="w-3/4 h-2 bg-gray-200 rounded mb-2" />
                          <div className="w-1/2 h-2 bg-gray-200 rounded" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* File Info Badge */}
                {logoFileInfo && !logoValidationError && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-green-50 border border-green-200 rounded-xl text-sm">
                    <CheckCircle size={16} className="text-green-500 flex-shrink-0" />
                    <span className="font-semibold text-green-700">{logoFileInfo.type}</span>
                    <span className="text-green-600">|</span>
                    <span className="text-green-700">{logoFileInfo.size}</span>
                    {logoFileInfo.width > 0 && (
                      <>
                        <span className="text-green-600">|</span>
                        <span className="text-green-700">{logoFileInfo.width}×{logoFileInfo.height}px</span>
                      </>
                    )}
                  </div>
                )}
                {logoValidationError && (
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 border border-red-200 rounded-xl text-sm">
                    <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                    <span className="text-red-700">{logoValidationError}</span>
                  </div>
                )}
              </div>

              {/* Right: Upload + Actions */}
              <div className="flex flex-col gap-4">
                {/* Drag & Drop Zone */}
                <div
                  className={`relative cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition-all ${isDraggingOver ? 'border-blue-500 bg-blue-50 scale-[1.01]' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'}`}
                  onClick={() => document.getElementById('logo-upload-input').click()}
                  onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                  onDragLeave={() => setIsDraggingOver(false)}
                  onDrop={handleDrop}
                >
                  <input id="logo-upload-input" type="file" className="hidden" accept=".png,.jpg,.jpeg,.svg" onChange={handleFileChange} />
                  <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-transform ${isDraggingOver ? 'bg-blue-200 scale-110' : 'bg-blue-100'}`}>
                    <CloudUpload size={28} className="text-blue-600" />
                  </div>
                  <p className="text-base font-semibold text-gray-800 mb-1">
                    {isDraggingOver ? t('companyAdmins.dropHere') : selectedLogoFile ? selectedLogoFile.name : t('companyAdmins.dragDropImage')}
                  </p>
                  <p className="text-sm text-gray-500">{t('companyAdmins.orClickBrowse')}</p>
                  {isDraggingOver && (
                    <div className="absolute inset-0 rounded-2xl bg-blue-500 bg-opacity-10 pointer-events-none" />
                  )}
                </div>

                {/* Crop/Resize Note */}
                {selectedLogoFile && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                    <Crop size={15} className="text-amber-600 flex-shrink-0" />
                    <span className="text-amber-700">
                      <strong>{t('companyAdmins.tipLabel')}:</strong> {t('companyAdmins.logoTip')}
                    </span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={!selectedLogoFile || isLogoUploading}
                    className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-white transition-all text-sm ${!selectedLogoFile || isLogoUploading ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-sky-500 to-fuchsia-500 hover:shadow-lg hover:scale-[1.02]'}`}
                  >
                    {isLogoUploading ? <><RefreshCw size={16} className="animate-spin" /> {t('companyAdmins.uploading')}</> : <><CloudUpload size={16} /> {activeCompany?.logo ? t('companyAdmins.replaceLogo') : t('companyAdmins.uploadLogo')}</>}
                  </button>
                  {selectedLogoFile && (
                    <button
                      onClick={() => { setSelectedLogoFile(null); setLogoPreviewUrl(null); setLogoFileInfo(null); setLogoValidationError(null); }}
                      className="px-5 py-3 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-all text-sm"
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                {/* Reset to Default */}
                {activeCompany?.logo && (
                  <button
                    onClick={handleResetLogo}
                    className="flex items-center justify-center gap-2 px-5 py-3 rounded-xl font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition-all text-sm"
                  >
                    <RotateCcw size={15} /> {t('companyAdmins.resetToDefaultLogo')}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };


  const renderUsersTab = () => {
    return (
      <div className="p-6">
        {/* Company Selection & Filters */}
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company Selection */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                {t('companyAdmins.selectCompany')}
              </label>
              <select
                value={selectedCompany || ''}
                onChange={handleCompanyChange}
                disabled={loading}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white disabled:bg-gray-50 disabled:cursor-not-allowed text-sm"
              >
                <option value="">{t('companyAdmins.chooseCompany')}</option>
                {filteredCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                {t('companyAdmins.searchUsers')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder={t('companyAdmins.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                />
              </div>
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                {t('companyAdmins.roleFilter')}
              </label>
              <select
                value={roleFilter}
                onChange={(e) => handleRoleFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white text-sm"
              >
                <option value="all">{t('companyAdmins.allRoles')}</option>
                <option value="admin">{t('companyAdmins.admin')}</option>
                <option value="employee">{t('companyAdmins.employee')}</option>
              </select>
            </div>

            {/* Clear Filters */}
            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium opacity-0">
                {t('companyAdmins.clear')}
              </label>
              {(searchTerm || roleFilter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <X size={16} />
                  {t('companyAdmins.clearFilters')}
                </button>
              )}
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || roleFilter !== 'all') && (
            <div className="flex flex-wrap gap-2">
              {searchTerm && (
                <div className="flex items-center gap-1 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  <Search size={12} />
                  {t('companyAdmins.search')} "{searchTerm}"
                </div>
              )}
              {roleFilter !== 'all' && (
                <div className="flex items-center gap-1 bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                  <Filter size={12} />
                  {t('companyAdmins.role')} {roleFilter}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Content */}
        {!selectedCompany ? (
          <div className="text-center py-12">
            <Building size={48} className="mx-auto mb-4 opacity-50 text-gray-400" />
            <h3 className="text-gray-900 mb-2 font-semibold">{t('companyAdmins.selectCompanyMessage')}</h3>
            <p className="text-gray-600 text-sm">
              {t('companyAdmins.selectCompanyDescription')}
            </p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw className="animate-spin text-blue-600" size={32} />
          </div>
        ) : (
          <>
            {/* Statistics Cards */}
            <CompanyStatsCards stats={stats} />

            {/* Users Table/Cards */}
            {sortedUsers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Users size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">{t('companyAdmins.noUsersFound')}</p>
                <p className="text-sm mt-1">
                  {searchTerm || roleFilter !== 'all'
                    ? t('companyAdmins.adjustFilters')
                    : t('companyAdmins.noUsersAdded')
                  }
                </p>
              </div>
            ) : isMobile ? (
              <CompanyUsersCards
                users={sortedUsers}
                onSort={handleSort}
                sortConfig={sortConfig}
                onDelete={(userId, userName) => openDeleteDialog(userId, userName)}
                onToggleUserManager={handleToggleUserManager}
                onOpenLoginActivity={(userId, userName) =>
                  setLoginActivityModal({
                    open: true,
                    userId,
                    userName: userName || '',
                    companyId: selectedCompany ?? null
                  })
                }
                onOpenTimeline={(userId, userName) => {
                  setTimelineUserId(userId);
                  setTimelineUserName(userName || '');
                  setTimelineOpen(true);
                }}
                isUserManager={!!userDetails?.is_usermanager}
                currentUserId={userDetails?.id}
                companyId={selectedCompany}
              />
            ) : (
              <CompanyUsersTable
                users={sortedUsers}
                onSort={handleSort}
                sortConfig={sortConfig}
                onDelete={(userId, userName) => openDeleteDialog(userId, userName)}
                onToggleUserManager={handleToggleUserManager}
                onOpenLoginActivity={(userId, userName) =>
                  setLoginActivityModal({
                    open: true,
                    userId,
                    userName: userName || '',
                    companyId: selectedCompany ?? null
                  })
                }
                onOpenTimeline={(userId, userName) => {
                  setTimelineUserId(userId);
                  setTimelineUserName(userName || '');
                  setTimelineOpen(true);
                }}
                isUserManager={!!userDetails?.is_usermanager}
                currentUserId={userDetails?.id}
                companyId={selectedCompany}
              />
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ paddingTop: '10px' }}>
      <NotificationPopup
        open={snackbar.open}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        message={snackbar.message}
      />

      <div className="max-w-8xl mx-auto p-2 sm:p-4">
        {/* Header */}
        <div className="mb-4">
          <div
            className="w-full px-4 py-3 rounded-xl text-white shadow-sm"
            style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #d946ef 100%)' }}
          >
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold mb-0.5 text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t('companyAdmins.pageTitle')}
              </h1>
              <p className="text-xs text-blue-100" style={{ fontFamily: "'Poppins', sans-serif" }}>
                {t('companyAdmins.pageSubtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {renderSectionHeader()}

          {/* Tab Content */}
          <div className="bg-white">
            {activeTab === 'users' && renderUsersTab()}
            {activeTab === 'credits' && <CreditsTab />}
            {activeTab === 'languages' && renderLanguagesTab()}
            {activeTab === 'appStatus' && renderAppStatusTab()}
            {activeTab === 'interviewConfiguration' && renderInterviewConfigurationTab()}
            {activeTab === 'companyLogo' && renderCompanyLogoTab()}
          </div>
        </div>
      </div>

      <InviteAdminModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleSendInvite}
        companyName={companies.find(c => c.id === selectedCompany)?.name}
      />

      <LanguageSelectionModal
        open={langModalOpen}
        onClose={() => setLangModalOpen(false)}
        currentLanguage={i18n.language}
        availableLanguages={userLanguagesForModal}
        onSelectLanguage={async (code) => {
          await i18n.changeLanguage(code);
          setDocumentDirection(code);
          setTimeout(() => {
            window.dispatchEvent(new Event('languagechange'));
          }, 100);
          setLangModalOpen(false);
        }}
      />

      <UserLoginActivityModal
        open={loginActivityModal.open}
        onClose={() => setLoginActivityModal({ open: false, userId: null, userName: '', companyId: null })}
        userId={loginActivityModal.userId}
        userName={loginActivityModal.userName}
        companyId={loginActivityModal.companyId}
      />

      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, userId: null, userName: '' })}
        onConfirm={handleConfirmDelete}
        title={t('companyAdmins.removeAdminTitle', 'Remove Admin')}
        message={t('companyAdmins.removeAdminConfirm', 'Are you sure you want to remove {{name}} as an admin from this company?', { name: deleteDialog.userName })}
      />

      {/* Timeline Drawer */}
      <Timeline
        open={timelineOpen}
        onClose={() => {
          setTimelineOpen(false);
          setTimelineUserId(null);
          setTimelineUserName('');
        }}
        apiEndpoint="audit/timeline/"
        apiParams={{
          module: 'companyadmin',
          ...(timelineUserId ? { entity_id: timelineUserId } : { company_id: selectedCompany })
        }}
        title={
          timelineUserName
            ? t('companyAdmins.userTimeline', { name: timelineUserName, defaultValue: `${timelineUserName} - Timeline` })
            : t('companyAdmins.adminTimeline', { defaultValue: 'Admin Timeline' })
        }
        pageName={t('companyAdmins.pageTitle')}
        width={450}
      />

      {/* Fixed Timeline Button */}
      {!isMobile && activeTab === 'users' && (
        <button
          onClick={() => {
            setTimelineUserId(null);
            setTimelineUserName('');
            setTimelineOpen(true);
          }}
          className={`fixed ${isRTL ? 'left-6' : 'right-6'} top-1/2 -translate-y-1/2 z-40 flex items-center justify-center text-white p-3 rounded-2xl transition-all duration-300 group hover:scale-110 hover:shadow-2xl`}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
          }}
        >
          <Clock size={16} />
          <span className={`absolute ${isRTL ? 'left-full ml-4' : 'right-full mr-4'} px-3 py-2 bg-gray-900/90 backdrop-blur-sm text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg`}>
            {t('companyAdmins.timeline', { defaultValue: 'Timeline' })}
          </span>
        </button>
      )}
    </div>
  );
};

export default CompanyAdmins;